import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import { 
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Print as PrintIcon,
  LocationOn as MapIcon,
} from '@mui/icons-material';
import { useOrders, useOrderStatusMutation } from '../services/api';
import { resolveImageUrl } from '../utils/imageUrl';
import { printOrders } from '../utils/print';

const getOrderInvoiceNumber = (order) => {
  console.log(order);
  return order?.invoiceNumber ?? order?.invoice_number ?? null;
}

const OrdersPage = () => {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 10,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  const { data: ordersData, isLoading, refetch } = useOrders(filters);
  const statusMutation = useOrderStatusMutation();

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === ordersData?.orders?.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(ordersData?.orders?.map(order => order.id) || []);
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleStatusChange = (isApproved) => {
    if (selectedOrders.length === 0) {
      setNotification({
        open: true,
        message: 'Please select at least one order',
        severity: 'warning'
      });
      return;
    }

    statusMutation.mutate(
      { orderIds: selectedOrders, isApproved },
      {
        onSuccess: () => {
          setNotification({
            open: true,
            message: `Orders ${isApproved ? 'approved' : 'rejected'} successfully!`,
            severity: 'success'
          });
          setSelectedOrders([]);
          refetch();
        },
        onError: (error) => {
          setNotification({
            open: true,
            message: `Failed to update orders: ${error.response?.data?.message || error.message}`,
            severity: 'error'
          });
        }
      }
    );
  };

  const handlePrintSelected = () => {
    if (selectedOrders.length === 0) {
      setNotification({
        open: true,
        message: 'Please select at least one order to print',
        severity: 'warning'
      });
      return;
    }

    const selectedOrdersData = ordersData?.orders?.filter(order => selectedOrders.includes(order.id)) || [];

    try {
      printOrders(selectedOrdersData, 'طباعة الطلبات');
      console.log(selectedOrdersData,"selectedOrdersData");
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Failed to open print dialog',
        severity: 'error',
      });
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field === 'page' ? value : 1
    }));
  };

  const getStatusColor = (isApproved) => {
    switch (isApproved) {
      case false: return 'warning';
      case true: return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getShopMapsUrl = (shop) => {
    const lat = shop?.lat;
    const lng = shop?.lng;
    if (lat == null || lng == null || lat === '' || lng === '') return null;
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };

  const handleOpenShopOnMap = (shop) => {
    const url = getShopMapsUrl(shop);
    if (!url) {
      setNotification({
        open: true,
        message: 'This shop has no location coordinates',
        severity: 'warning',
      });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">Orders</Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={() => handleStatusChange(true)}
              disabled={selectedOrders.length === 0 || statusMutation.isLoading}
            >
              Approve Selected
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<RejectIcon />}
              onClick={() => handleStatusChange(false)}
              disabled={selectedOrders.length === 0 || statusMutation.isLoading}
            >
              Reject Selected
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={<PrintIcon />}
              onClick={handlePrintSelected}
              disabled={selectedOrders.length === 0}
            >
              Print Selected
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Per Page</InputLabel>
                  <Select
                    value={filters.per_page}
                    onChange={(e) => handleFilterChange('per_page', e.target.value)}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedOrders.length > 0 && selectedOrders.length < ordersData?.orders?.length}
                        checked={selectedOrders.length === ordersData?.orders?.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Shop Name</TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Seller Code</TableCell>
                    <TableCell>Area</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Delivery Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordersData?.orders?.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedOrders.includes(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                          />
                        </TableCell>
                        <TableCell>{order.shop?.name || 'N/A'}</TableCell>
                        <TableCell>
                          {getOrderInvoiceNumber(order) ?? '—'}
                        </TableCell>
                        <TableCell>{order.shop?.sale?.code || 'N/A'}</TableCell>
                        <TableCell>{order.shop?.ammanArea?.name || 'N/A'}</TableCell>
                        <TableCell>{order.totalAmount} JD</TableCell>
                        <TableCell>
                          <Chip 
                            label={order.isApproved ? 'Approved' : 'Pending'} 
                            color={getStatusColor(order.isApproved)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(order.createdAt)}</TableCell>
                        <TableCell>{order.deliveryDate || 'N/A'}</TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewDetails(order)}
                            color="primary"
                            title="View details"
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenShopOnMap(order.shop)}
                            color="secondary"
                            title="Open shop on Google Maps"
                            disabled={!getShopMapsUrl(order.shop)}
                          >
                            <MapIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            {ordersData?.meta && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Typography variant="body2">
                  Showing {ordersData.meta.page} of {ordersData.meta.totalPages} pages 
                  ({ordersData.meta.total} total orders)
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    disabled={ordersData.meta.page <= 1}
                    onClick={() => handleFilterChange('page', ordersData.meta.page - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    disabled={ordersData.meta.page >= ordersData.meta.totalPages}
                    onClick={() => handleFilterChange('page', ordersData.meta.page + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Order Details Modal */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              {/* Order Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Order ID</Typography>
                  <Typography variant="body1">{selectedOrder.id}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Invoice #</Typography>
                  <Typography variant="body1">
                    {getOrderInvoiceNumber(selectedOrder) ?? '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Shop</Typography>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body1">{selectedOrder.shop?.name}</Typography>
                    {getShopMapsUrl(selectedOrder.shop) && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MapIcon />}
                        onClick={() => handleOpenShopOnMap(selectedOrder.shop)}
                      >
                        Google Maps
                      </Button>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Seller</Typography>
                  <Typography variant="body1">{selectedOrder.shop?.sale?.code}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Area</Typography>
                  <Typography variant="body1">{selectedOrder.shop?.ammanArea?.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Amount</Typography>
                  <Typography variant="h6" color="primary">{selectedOrder.totalAmount} JD</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={selectedOrder.isApproved ? 'Approved' : 'Pending'} 
                    color={getStatusColor(selectedOrder.isApproved)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Created</Typography>
                  <Typography variant="body1">{formatDate(selectedOrder.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Delivery Date</Typography>
                  <Typography variant="body1">{selectedOrder.deliveryDate || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                  <Typography variant="body1">{selectedOrder.notes || 'No notes'}</Typography>
                </Grid>
              </Grid>

              {/* Order Items */}
              <Typography variant="h6" sx={{ mb: 2 }}>Order Items</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Unit Price</TableCell>
                      <TableCell>Discount</TableCell>
                      <TableCell>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items?.map((item) => {
                      console.log(item,"item")
                      return (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={2}>
                              {item.productImageUrl && (
                                <img 
                                  src={resolveImageUrl(item.productImageUrl)} 
                                  alt={item.productName}
                                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                />
                              )}
                              <Box>
                                <Typography variant="body2">{item.productName}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {item.productNameArabic}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{((parseFloat(item.unitPrice) + (parseFloat(item.discount) * parseFloat(item.unitPrice) / 100)).toFixed(2)) * item.quantity}  JD</TableCell>
                          <TableCell>{item.discount} %</TableCell>
                          <TableCell>
                            {(parseFloat(item.unitPrice) * item.quantity)} JD
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrdersPage;
